import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, TextField, Button, Grid,
    Box, Divider, Alert, CircularProgress, MenuItem, InputAdornment,
    FormControlLabel, Checkbox, Snackbar
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../lib/axios';
import logo from '../../../src/assets/Images/Clanbridge-logo.png2.png';

const CheckoutBankDetailsForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [residentId, setResidentId] = useState(null);

    const [bankDetails, setBankDetails] = useState({
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        branchName: '',
        accountType: 'Savings',
        confirmAccountNumber: '',
        termsAccepted: false
    });


    const [errors, setErrors] = useState({});

    useEffect(() => {
        const dataParam = searchParams.get('data');
        if (dataParam) {
            try {
                const decodedData = JSON.parse(decodeURIComponent(dataParam));
                setFormData(decodedData);
                setResidentId(decodedData.residentId);

                setBankDetails(prev => ({
                    ...prev,
                    accountHolderName: decodedData.residentName || ''
                }));
            } catch (error) {
                toast.error('Invalid form data');
                navigate('/');
            }
        } else {
            toast.error('No data provided');
            navigate('/');
        }
    }, [searchParams, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBankDetails(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
 
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === 'accountNumber' && errors.confirmAccountNumber) {
            setErrors(prev => ({
                ...prev,
                confirmAccountNumber: ''
            }));
        }
    };

    const formatAccountNumber = (value) => {
        const digits = value.replace(/\D/g, '');
        const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
        return formatted;
    };

    const formatIfscCode = (value) => {
        return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!bankDetails.bankName.trim()) {
            newErrors.bankName = 'Bank name is required';
        } else if (bankDetails.bankName.trim().length < 3) {
            newErrors.bankName = 'Bank name must be at least 3 characters';
        }
        
        if (!bankDetails.accountHolderName.trim()) {
            newErrors.accountHolderName = 'Account holder name is required';
        } else if (bankDetails.accountHolderName.trim().length < 3) {
            newErrors.accountHolderName = 'Account holder name must be at least 3 characters';
        }
        
        if (!bankDetails.accountNumber.trim()) {
            newErrors.accountNumber = 'Account number is required';
        } else {
            const cleanAccountNumber = bankDetails.accountNumber.replace(/\s/g, '');
            if (!/^\d{9,18}$/.test(cleanAccountNumber)) {
                newErrors.accountNumber = 'Account number must be 9 to 18 digits';
            }
        }
        
        if (!bankDetails.confirmAccountNumber.trim()) {
            newErrors.confirmAccountNumber = 'Please confirm account number';
        } else {
            const cleanAccountNumber = bankDetails.accountNumber.replace(/\s/g, '');
            const cleanConfirmAccountNumber = bankDetails.confirmAccountNumber.replace(/\s/g, '');
            if (cleanAccountNumber !== cleanConfirmAccountNumber) {
                newErrors.confirmAccountNumber = 'Account numbers do not match';
            }
        }
        
        if (!bankDetails.ifscCode.trim()) {
            newErrors.ifscCode = 'IFSC code is required';
        } else {
            const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
            const cleanIfsc = bankDetails.ifscCode.replace(/\s/g, '');
            if (!ifscRegex.test(cleanIfsc)) {
                newErrors.ifscCode = 'Invalid IFSC code format (eg: SBIN0001234)';
            }
        }
        
        if (!bankDetails.termsAccepted) {
            newErrors.termsAccepted = 'You must accept the terms and conditions';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const updateResidentBankDetails = async () => {
        const cleanAccountNumber = bankDetails.accountNumber.replace(/\s/g, '');
        const cleanIfscCode = bankDetails.ifscCode.replace(/\s/g, '').toUpperCase();
        
        const payload = {
            ResidentId: residentId || formData?.residentId,
            BankName: bankDetails.bankName.trim(),
            AccountNo: cleanAccountNumber,
            IFSCCode: cleanIfscCode,
            BeneficiaryNm:bankDetails.accountHolderName
        };

        try {
            const response = await axiosInstance.post('ResidentMst/UpdateResidentBankDetails', payload);
            return response;
        } catch (error) {
            console.error('Error updating resident bank details:', error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please fix the errors before submitting');
            return;
        }

        setIsSubmitting(true);

        try {
            const updateResponse = await updateResidentBankDetails();

            if (updateResponse.data.Status === 0) {
                setIsSubmitted(true);
                toast.success('Bank details submitted successfully!');
                await sendConfirmationEmail();
                
                // setTimeout(() => {
                //     navigate('/');
                // }, 5000);
            } else {
                throw new Error(updateResponse.data.Message || 'Submission failed');
            }
        } catch (error) {
            console.error('Submission error:', error);
            toast.error(error.response?.data?.Message || error.message || 'Failed to submit bank details. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const sendConfirmationEmail = async () => {
        try {
            const cleanAccountNumber = bankDetails.accountNumber.replace(/\s/g, '');
            const maskedAccountNumber = cleanAccountNumber.substring(0, 4) + 
                '****' + 
                cleanAccountNumber.substring(cleanAccountNumber.length - 4);

            const emailData = {
                ToEmail: [formData?.email || 'support@clanbridge.com'],
                Subject: `Clanbridge Living - Bank Details Received [Booking: ${formData?.bookingNo}]`,
                Body: `
                    <html>
                        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
                                <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #635bff;">
                                    <h2 style="color: #635bff; margin: 0;">Clanbridge Living</h2>
                                    <p style="color: #666; margin-top: 5px;">Check-out Bank Details Submission</p>
                                </div>
                                
                                <div style="background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); padding: 20px; border-radius: 10px; margin-bottom: 25px;">
                                    <h3 style="color: #635bff; margin-top: 0; font-size: 18px;">✅ Submission Successful</h3>
                                    <p style="font-size: 14px; color: #555;">
                                        Thank you <strong>${formData?.residentName}</strong> for submitting your bank details.
                                    </p>
                                </div>
                                
                                <div style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 25px;">
                                    <h4 style="color: #1976d2; margin-top: 0; border-bottom: 1px solid #e0e0e0; padding-bottom: 10px;">
                                        Booking Information
                                    </h4>
                                    <table style="width: 100%; border-collapse: collapse;">
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold; width: 40%;">Booking No:</td>
                                            <td style="padding: 8px 0;">${formData?.bookingNo}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Resident Name:</td>
                                            <td style="padding: 8px 0;">${formData?.residentName}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Property:</td>
                                            <td style="padding: 8px 0;">${formData?.propertyName}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Check-out Date:</td>
                                            <td style="padding: 8px 0;">${formData?.expectedCheckoutDate}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Submission Date:</td>
                                            <td style="padding: 8px 0;">${new Date().toLocaleDateString('en-GB')}</td>
                                        </tr>
                                    </table>
                                </div>
                                
                                <div style="background: #e8f5e9; border: 1px solid #c8e6c9; border-radius: 8px; padding: 20px; margin-bottom: 25px;">
                                    <h4 style="color: #2e7d32; margin-top: 0; border-bottom: 1px solid #c8e6c9; padding-bottom: 10px;">
                                        Bank Account Details (Masked)
                                    </h4>
                                    <table style="width: 100%; border-collapse: collapse;">
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold; width: 40%;">Bank Name:</td>
                                            <td style="padding: 8px 0;">${bankDetails.bankName}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Account Holder:</td>
                                            <td style="padding: 8px 0;">${bankDetails.accountHolderName}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Account No:</td>
                                            <td style="padding: 8px 0;">${maskedAccountNumber}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">IFSC Code:</td>
                                            <td style="padding: 8px 0;">${bankDetails.ifscCode}</td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Account Type:</td>
                                            <td style="padding: 8px 0;">${bankDetails.accountType}</td>
                                        </tr>
                                        ${bankDetails.branchName ? `
                                        <tr>
                                            <td style="padding: 8px 0; font-weight: bold;">Branch:</td>
                                            <td style="padding: 8px 0;">${bankDetails.branchName}</td>
                                        </tr>` : ''}
                                    </table>
                                </div>
                                
                                <div style="background: #e3f2fd; border: 1px solid #bbdefb; border-radius: 8px; padding: 15px; margin-bottom: 25px;">
                                    <h5 style="color: #1565c0; margin-top: 0;">📋 Next Steps</h5>
                                    <ol style="margin: 10px 0; padding-left: 20px; color: #555; font-size: 14px;">
                                        <li style="margin-bottom: 8px;">Your security deposit refund will be processed within <strong>7-10 working days</strong></li>
                                        <li style="margin-bottom: 8px;">You will receive an email notification once the refund is initiated</li>
                                        <li style="margin-bottom: 8px;">Refund will be credited to the bank account provided above</li>
                                        <li>For any queries, contact us at <strong>sales@clanbridge.com</strong></li>
                                    </ol>
                                </div>
                                
                                <div style="text-align: center; padding-top: 20px; border-top: 1px solid #e0e0e0; color: #666; font-size: 12px;">
                                    <p>This is an automated confirmation. Please do not reply to this email.</p>
                                    <p>Clanbridge Living • ${new Date().getFullYear()}</p>
                                </div>
                            </div>
                        </body>
                    </html>
                `,
                PropId: formData?.propId || 0,
                UserId: 0 
            };
            
            await axios.post('/EmailRequest/SendEmail', emailData);
        } catch (error) {
            console.error('Failed to send confirmation email:', error);
        }
    };

    const handleReset = () => {
        setBankDetails({
            bankName: '',
            accountHolderName: formData?.residentName || '',
            accountNumber: '',
            ifscCode: '',
            branchName: '',
            accountType: 'Savings',
            confirmAccountNumber: '',
            termsAccepted: false
        });
        setErrors({});
    };

    if (isSubmitted) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
                    <Typography variant="h4" gutterBottom color="success.main">
                        Bank Details Submitted Successfully!
                    </Typography>
                    
                    <Box sx={{ mt: 4, mb: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            ✅ Confirmation Details
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Your bank details have been securely recorded for processing your security deposit refund.
                        </Typography>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Booking: {formData?.bookingNo}
                        </Typography>
                        <Typography variant="body2">
                            Resident: {formData?.residentName}
                        </Typography>
                    </Box>
                    
                    <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                        <Typography variant="body2">
                            <strong>Important:</strong> Your refund will be processed within 7-10 working days. 
                            You will receive an email confirmation once the refund is initiated.
                        </Typography>
                    </Alert>
                    
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 3 }}>
                        This page will redirect automatically in 5 seconds...
                    </Typography>
                    
                    <Button
                        variant="outlined"
                        sx={{ mt: 3 }}
                        onClick={() => navigate('/')}
                    >
                        Return to Home
                    </Button>
                </Paper>
            </Container>
        );
    }

    const RequiredLabel = ({ label }) => (
        <span>
            {label}
            <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
        </span>
    );

    return (
        <Container maxWidth="md" sx={{ mt: 2, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 2 }}>
                <Box sx={{ mb: 1, textAlign: 'center' }}>
                    <img 
                        src={logo} 
                        alt="Clanbridge Logo"
                        style={{ 
                            width: 'auto', 
                            maxWidth: '120px',
                            objectFit: 'contain'
                        }}
                    />
                    <Typography variant="h5" gutterBottom color="primary">
                        <AccountBalanceWalletIcon /> Fill The Bank Details 
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Please provide your bank account details to receive your security deposit refund.
                    </Typography>
                </Box>

                {formData && (
                    <Alert 
                        severity="info" 
                        sx={{ mb: 2 }}
                        icon={<PersonIcon />}
                    >
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                                Booking Information
                            </Typography>
                            <Typography variant="body2">
                                <strong>Booking:</strong> {formData.bookingNo} • 
                                <strong> Resident Name:</strong> {formData.residentName} • 
                                <strong> Property:</strong> {formData.propertyName}
                            </Typography>
                            {/* <Typography variant="caption" display="block">
                                Check-out Date: {formData.expectedCheckoutDate}
                            </Typography> */}
                        </Box>
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={<RequiredLabel label="Bank Name" />}
                                name="bankName"
                                value={bankDetails.bankName}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setBankDetails(prev => ({ ...prev, bankName: value }));
                                    if (errors.bankName) {
                                        setErrors(prev => ({ ...prev, bankName: '' }));
                                    }
                                }}
                                error={!!errors.bankName}
                                helperText={errors.bankName || "eg: State Bank of India, HDFC Bank, etc."}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AccountBalanceIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '40px',
                                    },
                                }}
                                placeholder="Enter bank name"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={<RequiredLabel label="Account Holder Name" />}
                                name="accountHolderName"
                                value={bankDetails.accountHolderName}
                                onChange={handleChange}
                                error={!!errors.accountHolderName}
                                helperText={errors.accountHolderName || "Name as per bank records"}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '40px',
                                    },
                                }}
                                placeholder="Enter account holder name"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={<RequiredLabel label="Account Number" />}
                                name="accountNumber"
                                value={bankDetails.accountNumber}
                                onChange={(e) => {
                                    const formatted = formatAccountNumber(e.target.value);
                                    setBankDetails(prev => ({ ...prev, accountNumber: formatted }));
                                    if (errors.accountNumber) {
                                        setErrors(prev => ({ ...prev, accountNumber: '' }));
                                    }
                                }}
                                error={!!errors.accountNumber}
                                helperText={errors.accountNumber || "9 to 18 digits"}
                                inputProps={{
                                    maxLength: 23,
                                    inputMode: 'numeric'
                                }}
                                className="custom-textfield"
                                placeholder="XXXX XXXX XXXX XXXX"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={<RequiredLabel label="Confirm Account Number" />}
                                name="confirmAccountNumber"
                                value={bankDetails.confirmAccountNumber}
                                onChange={(e) => {
                                    const formatted = formatAccountNumber(e.target.value);
                                    setBankDetails(prev => ({ ...prev, confirmAccountNumber: formatted }));
                                    if (errors.confirmAccountNumber) {
                                        setErrors(prev => ({ ...prev, confirmAccountNumber: '' }));
                                    }
                                }}
                                error={!!errors.confirmAccountNumber}
                                helperText={errors.confirmAccountNumber}
                                inputProps={{
                                    maxLength: 23,
                                    inputMode: 'numeric'
                                }}
                                className="custom-textfield"
                                placeholder="Re-enter account number"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={<RequiredLabel label="IFSC Code" />}
                                name="ifscCode"
                                value={bankDetails.ifscCode}
                                onChange={(e) => {
                                    const formatted = formatIfscCode(e.target.value);
                                    setBankDetails(prev => ({ ...prev, ifscCode: formatted }));
                                    if (errors.ifscCode) {
                                        setErrors(prev => ({ ...prev, ifscCode: '' }));
                                    }
                                }}
                                error={!!errors.ifscCode}
                                helperText={errors.ifscCode || "eg: SBIN0001234"}
                                inputProps={{
                                    maxLength: 11,
                                    style: { textTransform: 'uppercase' }
                                }}
                                className="custom-textfield"
                                placeholder="SBIN0001234"
                            />
                        </Grid>
                        
                        {/* <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Account Type"
                                name="accountType"
                                value={bankDetails.accountType}
                                onChange={handleChange}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '40px',
                                    },
                                }}
                            >
                                <MenuItem value="Savings">Savings Account</MenuItem>
                                <MenuItem value="Current">Current Account</MenuItem>
                                <MenuItem value="NRE">NRE Account</MenuItem>
                                <MenuItem value="NRO">NRO Account</MenuItem>
                            </TextField>
                        </Grid> */}
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Branch Name (Optional)"
                                name="branchName"
                                value={bankDetails.branchName}
                                onChange={handleChange}
                                helperText="City and branch location"
                                className="custom-textfield"
                                placeholder="eg: Mumbai Main Branch"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="termsAccepted"
                                            checked={bankDetails.termsAccepted}
                                            onChange={handleChange}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            I confirm that the bank account details provided above are correct and 
                                            belong to me. I authorize Clanbridge Living to process my security deposit 
                                            refund to this account. I understand that providing incorrect details may 
                                            delay the refund process.
                                            <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
                                        </Typography>
                                    }
                                />
                                {errors.termsAccepted && (
                                    <Typography color="error" variant="caption" sx={{ ml: 4, display: 'block' }}>
                                        {errors.termsAccepted}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'end', mt: 1 }}>
                               
                                
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {/* <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => navigate('/')}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button> */}
                                    
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                                        sx={{ minWidth: 150 }}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </form>

                <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="textSecondary">
                        <strong>Need help?</strong> Contact our support team at 
                        <strong> sales@clanbridge.com</strong>
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default CheckoutBankDetailsForm;